import { api, apiClient, tokenStorage, APIResponse } from './api';
import { User } from '@meradio/shared-types';

export interface LoginRequest {
  email: string;
  password: string;
}

export interface RegisterRequest {
  email: string;
  password: string;
  username?: string;
}

export interface TokenPair {
  accessToken: string;
  refreshToken: string;
}

export interface AuthResponse {
  user: User;
  tokens: TokenPair;
}

export interface RefreshTokenRequest {
  refreshToken: string;
}

export interface RefreshTokenResponse {
  tokens: TokenPair;
}

export interface ForgotPasswordRequest {
  email: string;
}

export interface ResetPasswordRequest {
  token: string;
  password: string;
}

export interface UpdateProfileRequest {
  username?: string;
}

export interface DeleteAccountRequest {
  password: string;
}

export interface ChangeEmailRequest {
  newEmail: string;
  password: string;
}

export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
}

export interface Session {
  id: string;
  deviceInfo: string;
  ipAddress: string;
  location?: string;
  lastActive: Date;
  isCurrent: boolean;
}

export interface GetSessionsResponse {
  sessions: Session[];
}

export const authAPI = {
  register: async (data: RegisterRequest): Promise<AuthResponse> => {
    const response = await api.post<AuthResponse>('/auth/register', data);
    
    if (response.success && response.data) {
      const { accessToken, refreshToken } = response.data.tokens;
      tokenStorage.setTokens(accessToken, refreshToken);
      
      return response.data;
    }
    
    throw new Error(response.message || 'Registration failed');
  },

  login: async (data: LoginRequest): Promise<AuthResponse> => {
    const response = await api.post<AuthResponse>('/auth/login', data);
    
    if (response.success && response.data) {
      const { accessToken, refreshToken } = response.data.tokens;
      tokenStorage.setTokens(accessToken, refreshToken);
      
      return response.data;
    }
    
    throw new Error(response.message || 'Login failed');
  },

  logout: async (): Promise<void> => {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      console.warn('Logout API call failed:', error);
    } finally {
      tokenStorage.clearTokens();
    }
  },

  getMe: async (): Promise<User> => {
    const response = await api.get<{ user: User }>('/auth/me');
    
    if (response.success && response.data) {
      return response.data.user;
    }
    
    throw new Error(response.message || 'Failed to fetch user profile');
  },

  refreshToken: async (refreshToken?: string): Promise<TokenPair> => {
    const token = refreshToken || tokenStorage.getRefreshToken();
    
    if (!token) {
      throw new Error('No refresh token available');
    }
    
    const response = await api.post<RefreshTokenResponse>('/auth/refresh', {
      refreshToken: token
    });
    
    if (response.success && response.data) {
      const { accessToken, refreshToken: newRefreshToken } = response.data.tokens;

      tokenStorage.setTokens(accessToken, newRefreshToken);
      
      return response.data.tokens;
    }
    
    throw new Error(response.message || 'Token refresh failed');
  },

  forgotPassword: async (data: ForgotPasswordRequest): Promise<void> => {
    const response = await api.post('/auth/forgot-password', data);

    if (!response.success) {
      throw new Error(response.message || 'Password reset request failed');
    }
  },

  resetPassword: async (data: ResetPasswordRequest): Promise<void> => {
    const response = await api.post('/auth/reset-password', data);

    if (!response.success) {
      throw new Error(response.message || 'Password reset failed');
    }
  },

  verifyEmail: async (token: string): Promise<void> => {
    const response = await api.get(`/auth/verify-email/${token}`);

    if (!response.success) {
      throw new Error(response.message || 'Email verification failed');
    }
  },

  resendVerification: async (): Promise<void> => {
    const response = await api.post('/auth/resend-verification');

    if (!response.success) {
      throw new Error(response.message || 'Failed to resend verification email');
    }
  },

  updateProfile: async (data: UpdateProfileRequest): Promise<User> => {
    const response = await api.put<{ user: User }>('/auth/profile', data);

    if (response.success && response.data) {
      return response.data.user;
    }

    throw new Error(response.message || 'Profile update failed');
  },

  deleteAccount: async (data: DeleteAccountRequest): Promise<void> => {
    const response = await api.delete('/auth/account', {
      data
    });

    if (!response.success) {
      throw new Error(response.message || 'Account deletion failed');
    }
  },

  changeEmail: async (data: ChangeEmailRequest): Promise<void> => {
    const response = await api.put('/auth/email', data);

    if (!response.success) {
      throw new Error(response.message || 'Email change failed');
    }
  },

  changePassword: async (data: ChangePasswordRequest): Promise<void> => {
    const response = await api.put('/auth/password', data);

    if (!response.success) {
      throw new Error(response.message || 'Password change failed');
    }
  },

  exportData: async (): Promise<Blob> => {
    const response = await apiClient.get('/auth/export-data', {
      responseType: 'blob'
    });

    return response.data;
  },

  getSessions: async (): Promise<Session[]> => {
    const response = await api.get<GetSessionsResponse>('/auth/sessions');

    if (response.success && response.data) {
      return response.data.sessions.map(session => ({
        ...session,
        lastActive: new Date(session.lastActive)
      }));
    }

    throw new Error(response.message || 'Failed to fetch sessions');
  },

  revokeSession: async (sessionId: string): Promise<void> => {
    const response = await api.delete(`/auth/sessions/${sessionId}`);

    if (!response.success) {
      throw new Error(response.message || 'Failed to revoke session');
    }
  },

  revokeAllSessions: async (): Promise<void> => {
    const response = await api.delete('/auth/sessions');

    if (!response.success) {
      throw new Error(response.message || 'Failed to revoke sessions');
    }
  }
};

export const authUtils = {
  isAuthenticated: (): boolean => {
    return tokenStorage.hasTokens();
  },

  getCurrentUser: async (): Promise<User | null> => {
    try {
      if (!tokenStorage.hasTokens()) {
        return null;
      }
      
      return await authAPI.getMe();
    } catch (error) {
      console.error('Failed to get current user:', error);
      return null;
    }
  },

  clearAuth: (): void => {
    tokenStorage.clearTokens();
  }
};
